import { isArray, isString } from 'lodash-es'
import PropTypes from 'prop-types'
import { Form } from 'react-bootstrap'
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome'
import { klass } from 'utils'
import './styles.scss'

/**
 * A checkbox with an icon inside of it.
 * If radio, the icon goes between the button and the label - radio buttons have their own UI meaning, so they're not mixed with the icon.
 * @param radio=false {bool} Creates radio buttons instead
 * @param name {string} Only needed for radio groups
 * @param id
 * @param icon {string|string[]|React.Element} FontAwesome icon. If it's an element, be sure to make it `fixedWidth`
 * @param defaultChecked {bool} If this is checked during load-time
 * @param currentlyChecked {bool} Any further verification to mark this as checked during runtime
 * @param onClick
 * @param label {JSX.Element|string} Either this or children elements
 * @param children {JSX.Element|string} Either this or a label prop
 * @return {JSX.Element}
 */
export default function IconCheck({ radio = false, name = '', id, icon, defaultChecked, currentlyChecked, onClick, label, children }) {
	if (!label && !children) {
		console.error(`Missing label or children for IconCheck (id=${id} icon=${icon})`)
	}
	if (radio && !name) {
		console.error(`Missing name for radio button (id=${id} icon=${icon})`)
	}
	const iconEl = isString(icon) || isArray(icon)? <Icon fixedWidth icon={icon}/> : icon

	return <Form.Check {...{ name, id, onClick, defaultChecked }}
	                   custom={radio} type={radio? 'radio' : 'checkbox'}
	                   {...klass('icon-check', { on: !radio && (defaultChecked || currentlyChecked) })}
	                   label={<>{iconEl} {label || children}</>}
	/>
}

IconCheck.propTypes = {
	radio           : PropTypes.bool,
	name            : PropTypes.string,
	id              : PropTypes.string.isRequired,
	icon            : PropTypes.oneOfType([PropTypes.node, PropTypes.string, PropTypes.arrayOf(PropTypes.string)]).isRequired,
	onClick         : PropTypes.func.isRequired,
	defaultChecked  : PropTypes.bool,
	currentlyChecked: PropTypes.bool,
	label           : PropTypes.node,
	children        : PropTypes.node,
}

/**
 * Creates a group of IconChecks.
 * If radio, the icon goes between the button and the label - radio buttons have their own UI meaning, so they're not mixed with the icon.
 * Kind of conflicts with {@link RadioGroup}.
 * @param radio=false {boolean} Creates radio buttons instead
 * @param statePair {array} Returned from useState() - if this is radio, the state will be a single value; if it's checkbox, it will always be an array, even if there's only a single element
 * @param options {Object<string[]>} An object which lists `[icon, label]` for each check
 * @param initial {string|string[]} A list of keys from `options` to be checked by default - should be used as default value for `statePair`'s `useState()`. Should be a single string if `radio=true`
 * @return {JSX.Element[]}
 */
IconCheck.Group = function IconCheckGroup ({ radio = false, statePair, options, initial = [] }) {
	let [selected, setSelected] = statePair
	selected ||= radio? undefined : []

	const onClick = radio?
		({ target }) => setSelected(target.id) //simple selection of the single clicked value
		:
		({ target }) => { //add/remove the clicked value and toggles the "on" state on the checkbox
			const index = selected.indexOf(target.id)
			if (index >= 0) {
				selected.splice(index, 1)
			} else {
				selected.push(target.id)
			}
			target.parentElement.classList.toggle('on')
			setSelected(selected)
		}

	return Object.entries(options).map(([id, [icon, label]]) => (
		<IconCheck {...{ radio, id, icon, label, onClick }}
		           key={id} name={radio?  `radio:${Object.keys(options).join('-')}` : null}
		           defaultChecked={initial.indexOf(id) >= 0}
		           currentlyChecked={radio? (selected == id) : selected.indexOf(id) >= 0}
		/>
	))
}
IconCheck.Group.propTypes = {
	radio    : PropTypes.bool,
	name     : PropTypes.string,
	statePair: PropTypes.array.isRequired,
	options  : PropTypes.objectOf(PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.node]))).isRequired,
	initial  : PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string])
}
